import React, { useState, useMemo, useEffect, useRef } from 'react';
import { PaperPlaneTiltIcon, WarningIcon, EnvelopeSimpleIcon, PhoneIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import classNames from '../../../../utils/classnames';
import { trackEvent } from '../../../../utils/track-event';

import { Dialog } from '../../../../components/dialog/Dialog';
import { Button } from '../../../../components/button/Button';
import { CheckboxWithLabel } from '../../../../components/checkbox/CheckboxWithLabel';
import { EmptyState } from '../../../../components/empty-state/EmptyState';
import { Alert } from '../../../../components/alert/Alert';
import { Input } from '../../../../components/input/Input';
import { ISimpleComboboxItem, SimpleCombobox } from '../../../../components/combobox/SimpleCombobox';
import type { PlanningBoardTruck, PlanningBoardTruckOrder, PlanningBoardTruckOrderPurchase } from '../../models';
import { sendPlanningBoardOrders } from '../../models';
import { formatCurrency } from '../../utils';
import { getDisplayError } from '../../../../utils/get-display-error';
import { splitProRata } from '../../../../utils/number';
import {
  calculateDieselSurchargeCents,
  resolveDieselSurchargeProductTypeId,
} from '../../../../domain/diesel-surcharge';
import {
  buildOrderCostLines,
  sumCostLines,
  KILOMETER_CHARGE_PRODUCT_TYPE_ID,
  type PlanningCostLine,
} from '../../../../domain/planning-board-costs';
import { getOrderKmBreakdown } from '../../../../utils/order-km';
import { useProductTypes } from '../../../order/models';
import { productTypeToComboboxItem } from '../../../order/productType/ProductTypeComboboxField';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { isPlanningBoardResend } from '../../utils';

// ============================================================================
// Types
// ============================================================================

interface SendOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  truck: PlanningBoardTruck;
  orders: PlanningBoardTruckOrder[];
  date: string;
  onSuccess: () => void;
}

interface PurchasePrice {
  orderId: string;
  amount: string;
}

/** A cost row the planner is editing: everything except transport and diesel. */
interface CostLineDraft {
  key: string;
  productTypeId: string;
  quantityInput: string;
  unitPriceInput: string;
}

// ============================================================================
// Helpers
// ============================================================================

/** Quantity input (e.g. "1.5") to the stored `amount` representation (150). */
const parseQuantity = (value: string): number => {
  const parsed = parseFloat(value);
  return !isNaN(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0;
};

/** Euro input (e.g. "45.00") to cents. */
const parseCents = (value: string): number => {
  const parsed = parseFloat(value);
  return !isNaN(parsed) && parsed >= 0 ? Math.round(parsed * 100) : 0;
};

const purchaseToDraft = (purchase: PlanningBoardTruckOrderPurchase): CostLineDraft => ({
  key: `purchase-${purchase.id}`,
  productTypeId: purchase.productTypeId,
  quantityInput: String(purchase.amount / 100),
  unitPriceInput: String(purchase.unitPrice / 100),
});

const draftToCostLine = (draft: CostLineDraft): PlanningCostLine => ({
  productTypeId: draft.productTypeId,
  amount: parseQuantity(draft.quantityInput),
  unitPrice: parseCents(draft.unitPriceInput),
});

// ============================================================================
// Component
// ============================================================================

export const SendOrderDialog: React.FC<SendOrderDialogProps> = ({
  isOpen,
  onClose,
  truck,
  orders,
  date,
  onSuccess,
}) => {
  const { settings } = useMinimalGeneralSettings();
  const { data: productTypes } = useProductTypes();

  // Transport, diesel and kilometerheffing keep their own inputs; every other
  // product type is a free-form cost row, so they must not show up twice.
  const transportProductTypeId = settings.defaultProductType?.id ?? null;
  const dieselProductTypeId = resolveDieselSurchargeProductTypeId(settings.dieselSurchargeProductType?.id);
  const kilometerChargeProductTypeId =
    productTypes == null || productTypes.some((productType) => productType.id === KILOMETER_CHARGE_PRODUCT_TYPE_ID)
      ? KILOMETER_CHARGE_PRODUCT_TYPE_ID
      : null;

  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [purchasePrices, setPurchasePrices] = useState<PurchasePrice[]>([]);
  const [totalPurchaseInput, setTotalPurchaseInput] = useState('');
  // null = follow the carrier's percentage, string = planner typed their own amount
  const [fuelOverrideInput, setFuelOverrideInput] = useState<string | null>(null);
  const [costsByOrder, setCostsByOrder] = useState<Record<string, CostLineDraft[]>>({});
  const [kilometerChargeByOrder, setKilometerChargeByOrder] = useState<Record<string, string>>({});
  const [planningNotes, setPlanningNotes] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Track previous open state to detect when dialog opens
  const wasOpenRef = useRef(false);

  const costTypeItems: ISimpleComboboxItem[] = useMemo(
    () =>
      (productTypes ?? [])
        .filter(
          (p) =>
            p.id !== transportProductTypeId && p.id !== dieselProductTypeId && p.id !== kilometerChargeProductTypeId,
        )
        .map(productTypeToComboboxItem),
    [productTypes, transportProductTypeId, dieselProductTypeId, kilometerChargeProductTypeId],
  );

  // Initialize state only when dialog first opens, not on every orders/contacts change
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justOpened && orders.length > 0) {
      // Every purchase line is edited here, so split them over the inputs that
      // own them: transport, diesel, kilometerheffing, and free-form cost rows.
      const transportCentsByOrder = new Map<string, number>();
      const nextCosts: Record<string, CostLineDraft[]> = {};
      const nextKilometerCharge: Record<string, string> = {};
      let existingFuel = 0;

      for (const order of orders) {
        let transportCents = 0;
        let kilometerChargeCents = 0;
        const drafts: CostLineDraft[] = [];

        for (const purchase of order.purchases) {
          const lineTotal = (purchase.amount / 100) * purchase.unitPrice;
          if (transportProductTypeId && purchase.productTypeId === transportProductTypeId) {
            transportCents += lineTotal;
          } else if (dieselProductTypeId && purchase.productTypeId === dieselProductTypeId) {
            existingFuel += lineTotal;
          } else if (purchase.productTypeId === kilometerChargeProductTypeId) {
            kilometerChargeCents += lineTotal;
          } else {
            drafts.push(purchaseToDraft(purchase));
          }
        }

        transportCentsByOrder.set(order.id, transportCents);
        nextCosts[order.id] = drafts;
        nextKilometerCharge[order.id] = kilometerChargeCents > 0 ? String(kilometerChargeCents / 100) : '';
      }

      setPurchasePrices(
        orders.map((order) => {
          const transportCents = transportCentsByOrder.get(order.id) ?? 0;
          return { orderId: order.id, amount: transportCents > 0 ? String(transportCents / 100) : '' };
        }),
      );
      setCostsByOrder(nextCosts);
      setKilometerChargeByOrder(nextKilometerCharge);
      setTotalPurchaseInput('');
      // Only treat a stored diesel surcharge as an override when it deviates from the
      // carrier's percentage — otherwise it keeps tracking the transport price.
      const existingPurchase = [...transportCentsByOrder.values()].reduce((sum, cents) => sum + cents, 0);
      const suggested = calculateDieselSurchargeCents(existingPurchase, truck.supplier.dieselSurchargePercentage);
      setFuelOverrideInput(existingFuel > 0 && existingFuel !== suggested ? String(existingFuel / 100) : null);
      setPlanningNotes('');
      // Auto-select contacts marked as transport order recipients
      const autoSelectContactIds = truck.supplier.contacts
        .filter((c) => c.shouldReceiveTransportOrders)
        .map((c) => c.id);
      setSelectedContactIds(autoSelectContactIds);
    }
  }, [
    isOpen,
    orders,
    truck.supplier.contacts,
    truck.supplier.dieselSurchargePercentage,
    transportProductTypeId,
    dieselProductTypeId,
    kilometerChargeProductTypeId,
  ]);

  // Transport price per order, in cents (excludes diesel and the extra costs)
  const purchaseCentsByOrder = useMemo(
    () =>
      orders.map((order) => {
        const priceEntry = purchasePrices.find((p) => p.orderId === order.id);
        return priceEntry ? Math.round(parseFloat(priceEntry.amount) * 100) || 0 : 0;
      }),
    [orders, purchasePrices],
  );
  const totalPurchaseCents = useMemo(
    () => purchaseCentsByOrder.reduce((sum, cents) => sum + cents, 0),
    [purchaseCentsByOrder],
  );

  // Suggested from the carrier's percentage, but the planner has the final say
  const suggestedFuelCents = calculateDieselSurchargeCents(
    totalPurchaseCents,
    truck.supplier.dieselSurchargePercentage,
  );
  const fuelInputValue = fuelOverrideInput ?? (suggestedFuelCents > 0 ? (suggestedFuelCents / 100).toFixed(2) : '');
  const totalFuelCents = useMemo(() => {
    if (fuelOverrideInput === null) {
      return suggestedFuelCents;
    }
    const parsed = parseFloat(fuelOverrideInput);
    return !isNaN(parsed) && parsed >= 0 ? Math.round(parsed * 100) : 0;
  }, [fuelOverrideInput, suggestedFuelCents]);

  // Spread the surcharge over the orders in proportion to their transport price
  const fuelCentsByOrder = useMemo(
    () => splitProRata(totalFuelCents / 100, purchaseCentsByOrder).map((euros) => Math.round(parseFloat(euros) * 100)),
    [totalFuelCents, purchaseCentsByOrder],
  );

  const costLinesByOrder = useMemo(
    () => orders.map((order) => (costsByOrder[order.id] ?? []).map(draftToCostLine)),
    [orders, costsByOrder],
  );
  const costCentsByOrder = useMemo(() => costLinesByOrder.map(sumCostLines), [costLinesByOrder]);
  const kilometerChargeCentsByOrder = useMemo(
    () =>
      orders.map((order) => {
        const parsed = parseFloat(kilometerChargeByOrder[order.id] ?? '');
        return !isNaN(parsed) && parsed >= 0 ? Math.round(parsed * 100) : 0;
      }),
    [orders, kilometerChargeByOrder],
  );
  const totalKilometerChargeCents = useMemo(
    () => kilometerChargeCentsByOrder.reduce((sum, cents) => sum + cents, 0),
    [kilometerChargeCentsByOrder],
  );

  // Calculate totals (transport + diesel + kilometerheffing + the extra costs)
  const totals = useMemo(() => {
    const totalSales = orders.reduce((sum, order) => sum + order.saleTotal, 0);
    const totalCosts = costCentsByOrder.reduce((sum, cents) => sum + cents, 0);
    const totalCost = totalPurchaseCents + totalFuelCents + totalKilometerChargeCents + totalCosts;
    const margin = totalSales - totalCost;
    const marginPercentage = totalSales > 0 ? Math.round((margin / totalSales) * 100) : 0;

    return {
      totalSales,
      totalPurchases: totalPurchaseCents,
      totalFuel: totalFuelCents,
      totalKilometerCharge: totalKilometerChargeCents,
      totalCosts,
      totalCost,
      margin,
      marginPercentage,
    };
  }, [orders, totalPurchaseCents, totalFuelCents, totalKilometerChargeCents, costCentsByOrder]);

  const handleContactToggle = (contactId: number) => {
    setSelectedContactIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId],
    );
  };

  const handlePurchasePriceChange = (orderId: string, amount: string) => {
    setPurchasePrices((prev) => prev.map((p) => (p.orderId === orderId ? { ...p, amount } : p)));
  };

  const handleTotalPurchaseChange = (value: string) => {
    setTotalPurchaseInput(value);
    const total = parseFloat(value);
    if (!isNaN(total) && total >= 0 && orders.length > 0) {
      const weights = orders.map((o) => o.saleTotal);
      const splits = splitProRata(total, weights);
      setPurchasePrices((prev) => prev.map((p, i) => ({ ...p, amount: splits[i] ?? p.amount })));
    }
  };

  const handleAddCostLine = (orderId: string) => {
    const firstCostType = costTypeItems[0];
    if (!firstCostType) {
      toast.error('Geen kostensoorten beschikbaar');
      return;
    }
    setCostsByOrder((prev) => ({
      ...prev,
      [orderId]: [
        ...(prev[orderId] ?? []),
        {
          key: `new-${orderId}-${(prev[orderId] ?? []).length}-${firstCostType.key}`,
          productTypeId: firstCostType.key,
          quantityInput: '1',
          unitPriceInput: '',
        },
      ],
    }));
  };

  const handleCostLineChange = (orderId: string, key: string, patch: Partial<CostLineDraft>) => {
    setCostsByOrder((prev) => ({
      ...prev,
      [orderId]: (prev[orderId] ?? []).map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));
  };

  const handleRemoveCostLine = (orderId: string, key: string) => {
    setCostsByOrder((prev) => ({
      ...prev,
      [orderId]: (prev[orderId] ?? []).filter((line) => line.key !== key),
    }));
  };

  const handleSend = async () => {
    if (selectedContactIds.length === 0) {
      toast.error('Selecteer minstens één contact');
      return;
    }

    setIsSending(true);
    try {
      await sendPlanningBoardOrders({
        orderIds: orders.map((o) => o.id),
        supplierTruckId: truck.id,
        date,
        contactIds: selectedContactIds,
        // Locked orders are read-only here, so leave their lines out entirely
        // instead of asking the server to replace them with what we rendered.
        orderCosts: orders
          .map((order, index) => ({
            orderId: order.id,
            purchaseLocked: order.purchaseLocked,
            lines: buildOrderCostLines({
              transportProductTypeId,
              transportCents: purchaseCentsByOrder[index] ?? 0,
              dieselProductTypeId,
              dieselCents: fuelCentsByOrder[index] ?? 0,
              kilometerChargeProductTypeId,
              kilometerChargeCents: kilometerChargeCentsByOrder[index] ?? 0,
              costLines: costLinesByOrder[index] ?? [],
            }),
          }))
          .filter((entry) => !entry.purchaseLocked)
          .map(({ orderId, lines }) => ({ orderId, lines })),
        planningNotes: planningNotes.trim() || undefined,
      });

      trackEvent('planning_board_orders_sent', {
        truck_id: truck.id,
        order_count: orders.length,
        contact_count: selectedContactIds.length,
      });

      toast.success(
        isPlanningBoardResend(orders)
          ? `${orders.length} order(s) opnieuw verstuurd`
          : `${orders.length} order(s) verstuurd`,
      );
      onSuccess();
    } catch (err) {
      toast.error(`Kon orders niet versturen: ${getDisplayError(err)}`);
    } finally {
      setIsSending(false);
    }
  };

  const contacts = truck.supplier.contacts;
  const rowGrid = 'grid grid-cols-[1.3fr_0.8fr_1.1fr_0.8fr_0.8fr_1.1fr] gap-2';

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={`Transportopdrachten versturen - ${truck.licensePlate}`}>
      <div className="space-y-4">
        {/* Orders with purchase prices */}
        <div>
          <label className="label-text mb-2 block">Orders ({orders.length})</label>
          {orders.length > 0 && (
            <div className="mb-2 flex items-center gap-2">
              <label className="label-text whitespace-nowrap">
                {orders.length > 1 ? 'Totaal vracht (pro rata):' : 'Totaal vracht:'}
              </label>
              <div className="flex-1">
                <Input
                  type="number"
                  value={totalPurchaseInput}
                  onChange={handleTotalPurchaseChange}
                  placeholder="bv. 732,00"
                />
              </div>
            </div>
          )}
          <div className="mb-2 flex items-center gap-2">
            <label className="label-text whitespace-nowrap">
              {truck.supplier.dieselSurchargePercentage > 0
                ? `Totaal dieseltoeslag (${(truck.supplier.dieselSurchargePercentage / 100).toFixed(2)} %):`
                : 'Totaal dieseltoeslag (optioneel):'}
            </label>
            <div className="flex-1">
              <Input type="number" value={fuelInputValue} onChange={setFuelOverrideInput} placeholder="0,00" />
            </div>
            {fuelOverrideInput !== null && suggestedFuelCents !== totalFuelCents && (
              <button
                type="button"
                className="link-text text-xs whitespace-nowrap"
                onClick={() => setFuelOverrideInput(null)}
              >
                Terug naar {(truck.supplier.dieselSurchargePercentage / 100).toFixed(2)} %
              </button>
            )}
          </div>

          <div className="border border-border rounded-lg overflow-hidden">
            {/* Header */}
            <div className={classNames(rowGrid, 'bg-surface-sunken px-3 py-2 text-xs font-medium text-fg-muted')}>
              <div>Order</div>
              <div className="text-right">Verkoop</div>
              <div className="text-right">Vracht</div>
              <div className="text-right">Diesel</div>
              <div className="text-right">Kosten</div>
              <div className="text-right">Marge</div>
            </div>
            {/* Rows */}
            <div className="divide-y divide-border">
              {orders.map((order, index) => {
                const priceEntry = purchasePrices.find((p) => p.orderId === order.id);
                const purchaseAmount = purchaseCentsByOrder[index] ?? 0;
                const fuelAmount = fuelCentsByOrder[index] ?? 0;
                const kilometerChargeAmount = kilometerChargeCentsByOrder[index] ?? 0;
                const costAmount = costCentsByOrder[index] ?? 0;
                const extraCostsAmount = costAmount + kilometerChargeAmount;
                const margin = order.saleTotal - purchaseAmount - fuelAmount - extraCostsAmount;
                const marginPercentage = order.saleTotal > 0 ? Math.round((margin / order.saleTotal) * 100) : 0;
                const costLines = costsByOrder[order.id] ?? [];
                const orderKm = getOrderKmBreakdown(order, order.lines);

                return (
                  <div key={order.id} className="px-3 py-2">
                    <div className={classNames(rowGrid, 'items-center')}>
                      <div>
                        <div className="font-medium text-sm">{order.orderNumber ?? 'NEW'}</div>
                        <div className="text-xs text-fg-muted">{order.customerRef}</div>
                      </div>
                      <div className="text-right text-sm text-brand-text">€{formatCurrency(order.saleTotal)}</div>
                      <div className="text-right">
                        {order.purchaseLocked ? (
                          <span className="text-sm text-fg-secondary">€{formatCurrency(purchaseAmount)}</span>
                        ) : (
                          <Input
                            type="number"
                            value={priceEntry?.amount ?? ''}
                            onChange={(val) => handlePurchasePriceChange(order.id, val)}
                            placeholder="0,00"
                          />
                        )}
                      </div>
                      <div className="text-right text-sm text-fg-secondary">€{formatCurrency(fuelAmount)}</div>
                      <div className="text-right text-sm text-fg-secondary">€{formatCurrency(extraCostsAmount)}</div>
                      <div
                        className={classNames(
                          'text-right text-sm font-medium',
                          margin >= 0 ? 'text-positive-text' : 'text-negative-text',
                        )}
                      >
                        €{formatCurrency(margin)}
                        <span className="text-xs text-fg-muted ml-1">({marginPercentage}%)</span>
                      </div>
                    </div>

                    {order.purchaseLocked ? (
                      <div className="mt-1 text-xs text-fg-muted">
                        Aankoopzijde geblokkeerd — het ritoverzicht of de self billing van deze order is al verzonden.
                      </div>
                    ) : (
                      <div className="mt-1 space-y-1">
                        {kilometerChargeProductTypeId && (
                          <div className="flex items-center gap-2 pl-4">
                            <div className="flex-1 min-w-0 text-xs text-fg-secondary">
                              Kilometerheffing
                              <span className="text-fg-muted ml-1">({orderKm.totalKm} km)</span>
                            </div>
                            <div className="w-24">
                              <Input
                                type="number"
                                value={kilometerChargeByOrder[order.id] ?? ''}
                                onChange={(val) =>
                                  setKilometerChargeByOrder((prev) => ({
                                    ...prev,
                                    [order.id]: val,
                                  }))
                                }
                                placeholder="0,00"
                              />
                            </div>
                          </div>
                        )}
                        {costLines.map((line) => (
                          <div key={line.key} className="flex items-center gap-2 pl-4">
                            <div className="flex-1 min-w-0">
                              <SimpleCombobox
                                items={costTypeItems}
                                selectedItem={
                                  costTypeItems.find((item) => item.key === line.productTypeId) ?? {
                                    key: line.productTypeId,
                                    name: line.productTypeId,
                                  }
                                }
                                onSelect={(item) => {
                                  if (item) {
                                    handleCostLineChange(order.id, line.key, { productTypeId: item.key });
                                  }
                                }}
                              />
                            </div>
                            <div className="w-16">
                              <Input
                                type="number"
                                value={line.quantityInput}
                                onChange={(val) => handleCostLineChange(order.id, line.key, { quantityInput: val })}
                                placeholder="1"
                              />
                            </div>
                            <span className="text-xs text-fg-muted">×</span>
                            <div className="w-24">
                              <Input
                                type="number"
                                value={line.unitPriceInput}
                                onChange={(val) => handleCostLineChange(order.id, line.key, { unitPriceInput: val })}
                                placeholder="0,00"
                              />
                            </div>
                            <button
                              type="button"
                              className="text-fg-muted hover:text-negative-text transition-colors"
                              aria-label="Kost verwijderen"
                              onClick={() => handleRemoveCostLine(order.id, line.key)}
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="flex items-center gap-1 pl-4 text-xs text-fg-muted hover:text-brand-text transition-colors"
                          onClick={() => handleAddCostLine(order.id)}
                        >
                          <PlusIcon className="w-3 h-3" />
                          Kost toevoegen
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Totals */}
            <div className={classNames(rowGrid, 'bg-surface-sunken px-3 py-2 text-sm font-medium')}>
              <div>Totaal</div>
              <div className="text-right text-brand-text">€{formatCurrency(totals.totalSales)}</div>
              <div className="text-right">€{formatCurrency(totals.totalPurchases)}</div>
              <div className="text-right">€{formatCurrency(totals.totalFuel)}</div>
              <div className="text-right">€{formatCurrency(totals.totalCosts + totals.totalKilometerCharge)}</div>
              <div
                className={classNames('text-right', totals.margin >= 0 ? 'text-positive-text' : 'text-negative-text')}
              >
                €{formatCurrency(totals.margin)}
                <span className="text-xs text-fg-muted ml-1">({totals.marginPercentage}%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Margin warning */}
        {totals.margin < 0 && (
          <Alert type="error" icon={<WarningIcon className="w-5 h-5" weight="fill" />}>
            Negatieve marge op deze planning!
          </Alert>
        )}

        {/* Notes */}
        <div>
          <label className="label-text mb-2 block">Notities voor vervoerder (optioneel)</label>
          <textarea
            value={planningNotes}
            onChange={(e) => setPlanningNotes(e.target.value)}
            placeholder="bv. neutraal laden, extra km info..."
            rows={2}
            className="w-full text-sm bg-surface border border-border rounded-md px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-brand-border resize-none"
          />
        </div>

        {/* Contact selection */}
        <div>
          <label className="label-text mb-2 block">Versturen naar ({selectedContactIds.length} geselecteerd)</label>
          {contacts.length === 0 ? (
            <EmptyState title={`Geen contacten beschikbaar voor ${truck.supplier.name}`} />
          ) : (
            <div className="space-y-3 max-h-40 overflow-auto">
              {contacts.map((contact) => (
                <div key={contact.id} className="border border-border rounded-lg p-3">
                  <CheckboxWithLabel
                    isChecked={selectedContactIds.includes(contact.id)}
                    onChange={() => handleContactToggle(contact.id)}
                    labelText={contact.name}
                  />
                  <div className="ml-6 mt-1 space-y-1">
                    <div className="flex items-center gap-1 text-xs text-fg-muted">
                      <EnvelopeSimpleIcon className="w-3 h-3" />
                      <span>{contact.email}</span>
                    </div>
                    {contact.phone && (
                      <div className="flex items-center gap-1 text-xs text-fg-muted">
                        <PhoneIcon className="w-3 h-3" />
                        <span>{contact.phone}</span>
                      </div>
                    )}
                    {contact.shouldReceiveTransportOrders && (
                      <span className="text-xs text-brand-text inline-block">Standaard transportcontact</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button color="secondary" onTrigger={onClose}>
            Annuleren
          </Button>
          <Button
            color="primary"
            onTrigger={handleSend}
            isLoading={isSending}
            isDisabled={selectedContactIds.length === 0}
            iconLeft={<PaperPlaneTiltIcon className="button-icon" />}
          >
            {isPlanningBoardResend(orders) ? 'Opnieuw versturen' : 'Verstuur'} {orders.length} order
            {orders.length > 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
